import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ChatBubbleOvalLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import PostFeed from '../components/PostFeed';
import { campusFeedAPI } from '../services/api';

const CampusFeedPage = () => {
  const [filters, setFilters] = useState({
    page: 1,
  });
  const [selectedPost, setSelectedPost] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch campus feed posts
  const { data, isLoading, error, refetch } = useQuery(
    ['campus-feed', filters.page],
    () => campusFeedAPI.getCampusFeed({ page: filters.page }),
    {
      keepPreviousData: true,
      refetchInterval: 30000, // Refresh every 30 seconds
      onSuccess: (data) => {
        console.log('React Query onSuccess - Campus feed data received:', data);
        console.log('React Query onSuccess - Response data structure:', {
          success: data?.success,
          dataKey: data?.data,
          posts: data?.data?.posts,
          postsLength: data?.data?.posts?.length
        });
      },
      onError: (error) => {
        console.error('React Query onError - Campus feed error:', error);
        console.error('React Query onError - Error response:', error?.response?.data);
      }
    }
  );

  // Delete post mutation
  const deleteMutation = useMutation(campusFeedAPI.deletePost, {
    onSuccess: () => {
      toast.success('Post deleted successfully');
      queryClient.invalidateQueries('campus-feed');
      setShowDeleteModal(false);
      setSelectedPost(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete post');
    },
  });

  const handleDeletePost = (post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedPost) {
      deleteMutation.mutate(selectedPost._id);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  if (isLoading) return <LoadingSpinner text="Loading campus feed..." />;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error loading campus feed</p>
        <button onClick={() => refetch()} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  const { posts = [], pagination = {} } = data?.data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Campus Feed</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor all posts across your college community
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="btn-secondary whitespace-nowrap"
        >
          Refresh Feed
        </button>
      </div>

      {/* Stats Card */}
      <div className="stats-card">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          <div className="flex items-center">
            <div className="bg-blue-500 rounded-full p-2 sm:p-3 mr-3 sm:mr-4 flex-shrink-0">
              <ChatBubbleOvalLeftIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">{pagination.total || 0}</div>
              <div className="text-xs sm:text-sm text-gray-500">Total Posts</div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="bg-red-500 rounded-full p-2 sm:p-3 mr-3 sm:mr-4 flex-shrink-0">
              <HeartSolidIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {posts.reduce((sum, post) => {
                  const likesCount = post.likesCount || (Array.isArray(post.likes) ? post.likes.length : 0);
                  return sum + likesCount;
                }, 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Total Likes</div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="bg-green-500 rounded-full p-2 sm:p-3 mr-3 sm:mr-4 flex-shrink-0">
              <ChatBubbleOvalLeftIcon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">
                {posts.reduce((sum, post) => {
                  const commentsCount = post.commentsCount || (Array.isArray(post.comments) ? post.comments.length : 0);
                  return sum + commentsCount;
                }, 0)}
              </div>
              <div className="text-xs sm:text-sm text-gray-500">Total Comments</div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Feed */}
      <PostFeed
        posts={posts}
        onDeletePost={handleDeletePost}
        showDeleteButton={true}
      />

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {(pagination.current - 1) * 20 + 1} to{' '}
            {Math.min(pagination.current * 20, pagination.total)} of {pagination.total} posts
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.current - 1)}
              disabled={!pagination.hasPrev}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-600">
              Page {pagination.current} of {pagination.pages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={!pagination.hasNext}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPost && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Delete Post</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this post by {selectedPost.author?.name}?
                This action cannot be undone.
              </p>
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <p className="text-sm text-gray-700 line-clamp-3">
                  {selectedPost.content}
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedPost(null);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteMutation.isLoading}
                  className="btn-danger"
                >
                  {deleteMutation.isLoading ? 'Deleting...' : 'Delete Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampusFeedPage;